import { FormInstance } from 'antd';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { routes } from 'routes';
import { useCreateCodeMutation } from 'services';
export const getExistProp = (data: { [key: string]: string | number }) => {
  const result = Object.keys(data).reduce((nextData, key) => {
    if (data[key]) {
      return {
        ...nextData,
        [key]: data[key],
      };
    }

    return nextData;
  }, {});

  return result;
};
export const formatCurrencyShort = (amount: number): string => {
  const isNegative = amount < 0;
  const absAmount = Math.abs(amount);

  if (absAmount >= 1_000_000_000) {
    return `${isNegative ? '-' : ''}${(absAmount / 1_000_000_000).toFixed(1)}T`;
  }
  if (absAmount >= 1_000_000) {
    return `${isNegative ? '-' : ''}${(absAmount / 1_000_000).toFixed(2)}Tr`;
  }
  if (absAmount >= 1_000) {
    return `${isNegative ? '-' : ''}${(absAmount / 1_000).toFixed(0)}K`;
  }
  return `${isNegative ? '-' : ''}${absAmount.toString()}`;
};
export const calculatePercentage = (value: number, total: number): number => {
  return total > 0 ? parseFloat(((value / total) * 100).toFixed(1)) : 0;
};
export const getQueryParams = (data: { [key: string]: string | number }) => {
  const query = Object.keys(data)
    .map((k) => encodeURIComponent(k) + '=' + encodeURIComponent(data[k] ?? ''))
    .join('&');

  return query;
};
export const roundMoney = (value: number): number => {
  if (isNaN(value)) return 0;
  return value % 1 >= 0.5 ? Math.ceil(value) : Math.floor(value);
};

export const formatMoney = (value: string): string => {
  const numericValue = value.replace(/[^\d.]/g, '');
  if (!numericValue) return '0';
  const [intPart, decimalPart] = numericValue.split('.');
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return decimalPart !== undefined ? `${formattedInt}.${decimalPart}` : formattedInt;
};
export function formatNumber(number: number | string) {
  return number.toLocaleString('vi-VN');
}
export const formatPrice = (price: number): string => {
  return new Intl.NumberFormat('en-US').format(price);
};
export const formatCurrency = (amount: number): string => {
  return `${formatPrice(amount)}`;
};
export const formatMoneyVN = (value?: number | string | null) => {
  if (value === null || value === undefined || value === '') return '';

  const num = Number(value);

  return new Intl.NumberFormat('vi-VN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(num);
};

export const formatQuantityVN = (value?: number | string | null) => {
  if (value === null || value === undefined || value === '') return '';

  const num = Number(value);

  return new Intl.NumberFormat('vi-VN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(num);
};

export const generateRandomBarcode = (length = 12): string => {
  return Array.from({ length }, () => Math.floor(Math.random() * 10)).join('');
};

export const formatDate = (date: Date | string | null | undefined): string => {
  if (!date) return '-';

  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    if (isNaN(dateObj.getTime())) {
      return '-';
    }
    return format(dateObj, "HH:mm 'Ngày' dd/MM/yyyy");
  } catch (error) {
    console.error('Error formatting date:', error);
    return '-';
  }
};
export const getRegion = () => {
  const region = process.env.REACT_APP_REGION;
  if (!region) throw new Error('Missing REGION env variable');
  return region;
};

export const getImageUploadBucket = () => {
  const bucket = process.env.REACT_APP_IMAGE_UPLOAD_BUCKET;
  if (!bucket) throw new Error('Missing IMAGE_UPLOAD_BUCKET env variable');
  return bucket;
};

export const getFilePath = () => {
  return `https://${getImageUploadBucket()}.s3.${getRegion()}.amazonaws.com`;
};
export const useBarcodeUtils = ({ form, code: initialCode }: { form?: FormInstance; code: string }) => {
  const [createcode] = useCreateCodeMutation();
  const [code, setCode] = useState(initialCode);
  const [barcode, setBarcode] = useState('');

  const handleCreateCode = useCallback(async () => {
    try {
      const res = await createcode({ code: initialCode }).unwrap();
      if (res) {
        if (form) {
          form.setFieldValue('code', res);
        }
        setCode(res); // lưu code mới vào state
      } else {
        console.error('Barcode creation failed, no data returned.');
      }
    } catch (err) {
      console.error('Barcode creation failed:', err);
    }
  }, [createcode, initialCode, form]);

  const handleBarcode = useCallback(() => {
    const randomBarcode = generateRandomBarcode();
    if (form) {
      form.setFieldValue('barcode', randomBarcode);
    }
    setBarcode(randomBarcode);
  }, [form]);

  return {
    handleCreateCode,
    handleBarcode,
    code,
    barcode,
  };
};
export const useCurrentRouteName = () => {
  const location = useLocation();

  const matchedRoute = routes.find((route) => route.path === location.pathname);

  if (matchedRoute && 'name' in matchedRoute && typeof (matchedRoute as { name?: unknown }).name === 'string') {
    return (matchedRoute as { name: string }).name;
  }
  return 'Unknown Route';
};
export const omit = <T, K extends keyof T>(obj: T, keys: K[]): Omit<T, K> => {
  const result = { ...obj };
  keys.forEach((key) => delete result[key]);
  return result;
};
export const downloadFileFromUrl = async (url: string, filename: string): Promise<void> => {
  try {
    // Fetch the file using axios with responseType blob
    const response = await axios.get(url, {
      responseType: 'blob',
    });

    // Create blob URL and trigger download
    const blob = response.data;
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Error downloading file from URL:', error);
    throw error;
  }
};
